import React from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, MapPin, Tag, Heart } from "lucide-react";
import { avgScore, distinctionOf, DISTINCTIONS, CRITERIA } from "../mock";
import { usePlaces } from "../context/PlacesContext";
import { ScoreMeter, DistinctionMark, PlaceCard, SectionLabel } from "../components/Bits";
import Seo from "../components/Seo";

export default function PlaceDetail() {
  const { slug } = useParams();
  const { places, getPlace, loading, isFav, toggleFav } = usePlaces();
  const place = getPlace(slug);

  if (loading) {
    return <div className="pt-[140px] pb-40 text-center text-ink-soft font-serif text-2xl">Loading…</div>;
  }
  if (!place) {
    return (
      <div className="pt-[140px] pb-40 text-center">
        <h1 className="font-serif text-4xl">Place not found.</h1>
        <Link to="/explore" className="inline-block mt-6 text-claret link-underline">Back to Explore</Link>
      </div>
    );
  }

  const score = avgScore(place.scores);
  const level = distinctionOf(place);
  const meta = DISTINCTIONS[level];
  const fav = isFav(place.slug);
  const more = places.filter((p) => p.slug !== slug && p.neighborhood === place.neighborhood);
  const fallback = places.filter((p) => p.slug !== slug && p.city === place.city);
  const related = (more.length >= 2 ? more : fallback).slice(0, 4);
  const mapQuery = `${place.name}, ${place.neighborhood}, ${place.city}, ${place.country}`;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Restaurant",
    name: place.name,
    image: place.image,
    servesCuisine: place.cuisine,
    priceRange: place.price,
    address: {
      "@type": "PostalAddress",
      addressLocality: place.city,
      addressRegion: place.region,
      addressCountry: place.country,
    },
    review: {
      "@type": "Review",
      reviewBody: place.excerpt,
      author: { "@type": "Organization", name: "Placeworthy" },
    },
  };

  return (
    <div className="pt-[68px]">
      <Seo
        title={`${place.name} — ${place.city} ${place.category} Review`}
        description={place.excerpt}
        path={`/place/${place.slug}`}
        image={place.image}
        jsonLd={jsonLd}
      />
      <section className="relative h-[62vh] min-h-[440px] flex items-end overflow-hidden">
        <div className="absolute inset-0 kenburns">
          <img src={place.image} alt={place.name} className="w-full h-full object-cover" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/30 to-ink/20" />
        <div className="relative max-w-[1100px] mx-auto px-6 lg:px-10 w-full pb-12">
          <Link to="/reviews" className="inline-flex items-center gap-2 text-paper/70 hover:text-paper text-sm mb-6">
            <ArrowLeft className="w-4 h-4" /> All reviews
          </Link>
          <div className="flex flex-wrap items-center gap-3">
            {level > 0 ? (
              <span className="inline-flex items-center gap-2.5 bg-paper/10 backdrop-blur-sm px-4 py-2">
                <span className="flex items-center gap-1 shrink-0">
                  {Array.from({ length: level }).map((_, i) => (
                    <img key={i} src="/placeworthy-mark.png" alt="" aria-hidden="true" className="invert w-5 h-5 sm:w-6 sm:h-6" />
                  ))}
                </span>
                <span className="text-paper font-serif font-semibold leading-tight whitespace-nowrap">
                  {meta.label}
                  <span className="block eyebrow text-[9px] text-brass mt-0.5 font-sans whitespace-normal">{meta.sub}</span>
                </span>
              </span>
            ) : (
              <span className="eyebrow text-[10px] text-paper/70 border border-paper/25 px-3 py-1.5">Reviewed</span>
            )}
            <button
              onClick={() => toggleFav(place.slug)}
              className={`inline-flex items-center gap-2 px-4 py-2 text-[11px] font-semibold tracking-[0.14em] uppercase shrink-0 transition-colors ${fav ? "bg-claret text-paper" : "bg-paper/20 text-paper backdrop-blur-sm hover:bg-paper/30"}`}
            >
              <Heart className="w-3.5 h-3.5" fill={fav ? "currentColor" : "none"} /> {fav ? "Saved" : "Save"}
            </button>
          </div>
          <h1 className="font-serif text-paper text-4xl sm:text-5xl md:text-7xl font-semibold mt-4 leading-none">{place.name}</h1>
          {place.accolades?.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 mt-4">
              {place.accolades.map((a) => (
                <span key={a} className="bg-brass text-ink text-[10px] font-semibold tracking-wide uppercase px-3 py-1.5">{a}</span>
              ))}
            </div>
          )}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-paper/80 mt-4 text-sm">
            <span className="inline-flex items-center gap-1.5"><MapPin className="w-4 h-4" /> {place.neighborhood} · {place.city} · {place.country}</span>
            <span>·</span><span>{place.cuisine}</span>
            <span>·</span><span>{place.price}</span>
          </div>
        </div>
      </section>

      <div className="max-w-[1100px] mx-auto px-6 lg:px-10 py-16 grid lg:grid-cols-[1fr_320px] gap-16">
        <article>
          <div className="flex items-start gap-4 pb-8 border-b border-ink/10">
            {level > 0 ? (
              <span className="flex items-center gap-1.5 shrink-0">
                {Array.from({ length: level }).map((_, i) => (
                  <img key={i} src="/placeworthy-mark.png" alt="" aria-hidden="true" className="w-8 h-8 sm:w-10 sm:h-10" />
                ))}
              </span>
            ) : null}
            <div className="min-w-0 flex-1">
              <span className="eyebrow text-claret text-[10px]">The distinction</span>
              <p className="font-serif text-2xl sm:text-3xl font-semibold leading-tight">{level > 0 ? meta.label : "Reviewed"}</p>
              <p className="text-ink-soft text-sm mt-1">
                {level > 0 ? meta.sub : "A full Placeworthy review"} · by {place.author}, {place.date}
              </p>
            </div>
          </div>

          <p className="font-serif text-2xl md:text-3xl leading-snug mt-10 mb-8 display-balance">{place.excerpt}</p>
          {(place.body || []).map((para, i) => (
            <p key={i} className="text-ink-soft text-lg leading-[1.8] mb-6">{para}</p>
          ))}

          {place.gallery?.length > 0 && (
            <div className="grid grid-cols-3 gap-3 mt-10">
              {place.gallery.map((g, i) => (
                <div key={i} className="img-zoom aspect-square bg-ink/5">
                  <img src={g} alt={`${place.name} ${i + 1}`} className="w-full h-full object-cover" loading="lazy" />
                </div>
              ))}
            </div>
          )}

          {place.tags?.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 mt-10">
              <Tag className="w-4 h-4 text-ink-soft" />
              {place.tags.map((t) => (
                <span key={t} className="px-3 py-1 bg-ink/5 text-xs font-medium text-ink-soft">{t}</span>
              ))}
            </div>
          )}
        </article>

        <aside className="lg:sticky lg:top-24 self-start space-y-8">
          <div className="border border-ink/12 p-6 bg-paper">
            <span className="eyebrow text-claret text-[10px]">The scorecard</span>
            <h3 className="font-serif text-2xl font-semibold mt-1 mb-3">Five criteria</h3>
            {CRITERIA.map((c) => (
              <ScoreMeter key={c.key} label={c.label} value={place.scores[c.key]} />
            ))}
            <div className="flex items-center justify-between mt-5 pt-4">
              <span className="font-serif text-lg font-semibold">Distinction</span>
              {level > 0 ? (
                <span className="flex items-center gap-1">
                  {Array.from({ length: level }).map((_, i) => (
                    <img key={i} src="/placeworthy-mark.png" alt="" aria-hidden="true" className="w-6 h-6" />
                  ))}
                </span>
              ) : (
                <span className="eyebrow text-[10px] text-ink-soft">None</span>
              )}
            </div>
          </div>

          <div className="border border-ink/12 overflow-hidden bg-paper">
            <iframe
              title="Map"
              src={`https://maps.google.com/maps?q=${encodeURIComponent(mapQuery)}&z=14&output=embed`}
              className="w-full h-52 grayscale-[0.15]"
              loading="lazy"
              style={{ border: 0 }}
            />
            <div className="p-4">
              <span className="eyebrow text-ink-soft text-[10px]">Location</span>
              <p className="font-medium">{place.neighborhood}, {place.city}</p>
            </div>
          </div>
        </aside>
      </div>

      {related.length > 0 && (
        <section className="bg-paper-dark relative grain">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 relative">
            <SectionLabel>More in {place.city}</SectionLabel>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-14 mt-10">
              {related.map((p, i) => <PlaceCard key={p.id} place={p} index={i} />)}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
